import React, { useState, useMemo } from "react";
import { Tooltip } from "@mui/material";
import { BUPopover } from "views/pages/manage/unAssigned/studentFinanceApproval/epmrCancellation/constant";
import { LastUpdatedDetails, getFormattedDate, getUpdatedTime } from "views/pages/manage/employeeManagement/userProfile/helper";
import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";
import styles from "../../paymentManagement.module.scss";
import ALoader from "views/pages/manage/common/a-loader";
import { useNavigateToGivenPath } from "views/pages/manage/edpOps/studentDetails/paymentSummary/service";
import { CourseTermPopover } from "views/pages/manage/unAssigned/studentFinanceApproval/epmrCancellation/constant";
import { getCashDepositDetails } from "./utils";

const ReceiptListComponent = ({
  rows,
  paginationInfo,
  loading,
  setLoading,
  dropdowns,
  cashReceiptId,
  setPaginationInfo,
  setDetails
}) => {

  const [buDescriptionMap, setBuDescriptionMap] = useState({});
  const [courseMap, setCourseMap] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigateToGivenPath = useNavigateToGivenPath();

  const columns = useMemo(() => [
    {
      key: "businessAreaValue",
      label: "BU",
      sortable: true,

      width: 120,
      render: (value, row) => (
        value ? (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">
              <div>
                <span>{value}</span>&nbsp;
                <span className="cursor">
                  <BUPopover
                    businessArea={value}
                    businessAreaOptions={dropdowns?.businessAreas}
                    buDescriptionMap={buDescriptionMap}
                    setBuDescriptionMap={setBuDescriptionMap}
                  />
                </span>
              </div>
            </div>
          </Tooltip>
        ) : <div>-</div>
      )
    },
    {
      key: "cashierOffice",
      label: "Cashier Office",
      width: 120,
      render: (value, row) => (
        value ? (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">
              <div>
                <span>{value}</span>&nbsp;
                <span className="cursor">
                  <BUPopover
                    businessArea={value}
                    businessAreaOptions={dropdowns?.businessAreas}
                    buDescriptionMap={buDescriptionMap}
                    setBuDescriptionMap={setBuDescriptionMap}
                  />
                </span>
              </div>
            </div>
          </Tooltip>
        ) : <div>-</div>
      )
    },
    {
      // cash always
      key: "type",
      sortable: true,
      label: "Type",
      width: 120,
      render: (value) => (
        value ? <Tooltip title={value}><div className="ellipsis-text-container">{value}</div></Tooltip> : <div>-</div>
      )
    },
    {
      key: "psid",
      sortable: true,
      label: "PSID",
      width: 120,
      render: (value) => (
        value ? <Tooltip title={value}>
          <div
            onClick={() => { navigateToGivenPath(`admin/edp/studentDetails/${value}`) }}
            style={{ color: "#00B0F5", cursor: "pointer" }}
            className="ellipsis-text-container">{value}</div></Tooltip> : <div>-</div>
      )
    },
    {
      key: "studentName",
      sortable: true,
      label: "Student Name",
      width: 120,
      render: (value) => (
        value ? <Tooltip title={value}><div className="ellipsis-text-container">{value}</div></Tooltip> : <div>-</div>
      )
    },
    {
      key: "term",
      sortable: true,
      label: "Term",
      width: 120,
      render: (value) => (
        value ? <Tooltip title={value}><div className="ellipsis-text-container">{value}</div></Tooltip> : <div>-</div>
      )
    },
    {
      key: "receiptNo",
      sortable: true,
      label: "Receipt No.",
      width: 120,
      render: (value) => (
        value ? <Tooltip title={value}><div className="ellipsis-text-container">{value}</div></Tooltip> : <div>-</div>
      )
    },
    {
      key: "courseId",
      sortable: true,
      label: "Course",
      width: 120,
      render: (value, row) => (
        value ? (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">
              <div>
                <span>{value}</span>&nbsp;
                <span className="cursor">
                  <CourseTermPopover
                    courseId={value}
                    term={row?.term}
                    courseTermMap={courseMap}
                    setCourseTermMap={setCourseMap}
                  />
                </span>
              </div>
            </div>
          </Tooltip>
        ) : <div>-</div>
      )
    },
    {
      key: "receiptDate",
      sortable: true,
      label: "Receipt date & time",
      width: 200,
      render: (value) => {
        const display = `${getFormattedDate(value)} at ${getUpdatedTime(value)}`;
        return value ? <Tooltip title={display}><div className="ellipsis-text-container">{display}</div></Tooltip> : <div>-</div>
      }
    },
    {
      key: "lastUpdatedOn",
      sortable: true,
      label: "Last updated on",
      width: 120,
      render: (value, row) => {
        const display = LastUpdatedDetails(value, row?.lastUpdatedBy);
        return value ? <Tooltip title={display}><div className="ellipsis-text-container">{display}</div></Tooltip> : <div>-</div>
      }
    },
    {
      key: "approvedOn",
      sortable: true,
      label: "Approved on",
      width: 120,
      render: (value, row) => {
        const display = LastUpdatedDetails(value, row?.approvedBy);
        return value ? <Tooltip title={display}><div className="ellipsis-text-container">{display}</div></Tooltip> : <div>-</div>
      }
    },
    {
      key: "amountCollected",
      align: "right",
      sortable: true,
      label: "Amount Collected",
      width: 120,
      pinned: true,
      render: (value) => (
        <Tooltip title={value}>
          <div className="ellipsis-text-container">
            <div className={styles['amount']}>{formattedPrice(value)}</div>
          </div>
        </Tooltip>
      )
    },
  ], [dropdowns, buDescriptionMap, navigateToGivenPath])

  const pagination = {
    page: (paginationInfo?.currPageNo ?? 0) + 1,
    pageSize: paginationInfo?.currPageSize || 10,
    total: paginationInfo?.totalRecords || 0,
    totalPages: paginationInfo?.totalPage || 0
  };

  // --- Handlers ---
  const handlePageChange = async (newPage) => {
    try {
      setLoading(true);

      const offset = newPage - 1;
      const reqObj = {
        depositSlipId: Number(cashReceiptId)
      }
      const resp = await getCashDepositDetails({ pageNo: offset, pageSize: pagination.pageSize, setPaginationInfo, reqObj, fetchLogs: true });
      setDetails(resp);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    try {
      setLoading(true);
      const reqObj = {
        depositSlipId: Number(cashReceiptId)
      }
      const resp = await getCashDepositDetails({ pageNo: 0, pageSize: newSize, setPaginationInfo, reqObj, fetchLogs: true });
      setDetails(resp);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <ALoader position="fixed" />}
      <ServerDataGrid
        rows={rows || []}
        loading={false}
        columns={columns}
        rowKey="id"
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        sortConfig={sortConfig}
        onSortChange={(key, direction) => setSortConfig({ key, direction })}
      />
    </>
  )
}

export default ReceiptListComponent;